import React from 'react';

/**
 * Renders the appropriate input control for a single follow-up question.
 *
 * Centralises the answer_type switch so BasicsStep / FollowupsStep don't have
 * to deal with the dispatch (and so adding a new answer type — e.g. a number
 * picker — is a one-file change).
 */
function FollowupInput({ question, value, onChange }) {
  if (question.answer_type === 'single_choice' && Array.isArray(question.choices)) {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— select —</option>
        {question.choices.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    );
  }
  if (question.answer_type === 'long_text') {
    return (
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Step 2 — tailored follow-up questions returned by the LLM.
 *
 * Props:
 *   questions:    array of { id, question, rationale, answer_type, choices }
 *   answers:      object keyed by question id
 *   onAnswer:     (id, value) => void
 *   loading:      boolean
 *   onBack:       () => void
 *   onNext:       () => void
 */
export default function FollowupsStep({
  questions,
  answers,
  onAnswer,
  loading,
  onBack,
  onNext,
}) {
  return (
    <section className="goal-step">
      <h2>A few quick questions</h2>
      <p className="goal-step-hint">
        These help us tailor the plan. Leave any blank if you&apos;re not sure.
      </p>

      {questions.length === 0 && (
        <p className="goal-step-hint">No follow-ups needed — you can continue.</p>
      )}

      {questions.map((q) => (
        <div key={q.id} className="goal-field">
          <span>{q.question}</span>
          {q.rationale && <small className="goal-rationale">{q.rationale}</small>}
          <FollowupInput
            question={q}
            value={answers[q.id] || ''}
            onChange={(v) => onAnswer(q.id, v)}
          />
        </div>
      ))}

      <div className="goal-actions">
        <button type="button" className="goal-btn-secondary" onClick={onBack}>
          ← Back
        </button>
        <button
          type="button"
          className="goal-btn-primary"
          onClick={onNext}
          disabled={loading}
        >
          {loading ? 'Drafting plan…' : 'Generate plan →'}
        </button>
      </div>
    </section>
  );
}
